/**
 * AuthProvider Component
 *
 * Wraps the application with MSAL authentication context.
 * Provides authentication state and methods to child components.
 */

import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { apiClient } from '@medsuite/api-client';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { msalConfig, loginRequest, apiRequest, validateAuthConfig } from '../config/auth.config';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().then(() => {
  // Handle redirect promise (for redirect flow)
  msalInstance
    .handleRedirectPromise()
    .then((response) => {
      if (response) {
        msalInstance.setActiveAccount(response.account);

        // Check if this was a consent redirect and restore location
        const graphRedirectPath = sessionStorage.getItem('graph_consent_redirect');
        const apiRedirectPath = sessionStorage.getItem('api_consent_redirect');
        const redirectPath = graphRedirectPath || apiRedirectPath;

        if (redirectPath) {
          sessionStorage.removeItem('graph_consent_redirect');
          sessionStorage.removeItem('api_consent_redirect');
          // Use replaceState to avoid adding to history
          window.history.replaceState(null, '', redirectPath);
        }
      }
    })
    .catch((error) => {
      console.error('Error handling redirect:', error);
      // Clean up redirect paths on error too
      sessionStorage.removeItem('graph_consent_redirect');
      sessionStorage.removeItem('api_consent_redirect');
    });

  // Set active account if available
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // Listen for sign-in events
  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
    }
  });
});

/**
 * Decode JWT token payload without validation
 * Used for extracting claims client-side
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Auth context type
 */
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AccountInfo | null;
  roles: string[];
  hasRole: (role: string) => boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Inner auth provider that uses MSAL hooks
 */
function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);

  const user = accounts.length > 0 ? accounts[0] : null;

  // Set loading state based on MSAL progress
  useEffect(() => {
    if (inProgress === 'none') {
      setIsLoading(false);
    }
  }, [inProgress]);

  // Update API client with access token when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Register token refresh callback so API client can get fresh tokens before each request
      // This ensures tokens are automatically refreshed before they expire
      apiClient.setTokenRefreshCallback(async () => {
        console.warn('[Auth] Token refresh callback invoked, requesting scopes:', apiRequest.scopes);
        try {
          const response = await instance.acquireTokenSilent({
            ...apiRequest,
            account: user,
          });
          console.warn(
            '[Auth] Token acquired successfully, token length:',
            response.accessToken?.length
          );
          return response.accessToken;
        } catch (error: unknown) {
          const msalError = error as { constructor?: { name?: string }; errorCode?: string; message?: string };
          console.error('Token refresh silent acquisition failed:', {
            errorType: msalError?.constructor?.name,
            errorCode: msalError?.errorCode,
            errorMessage: msalError?.message,
          });

          // If silent fails due to consent/interaction required, try popup
          if (
            error instanceof InteractionRequiredAuthError ||
            msalError?.errorCode === 'consent_required' ||
            msalError?.errorCode === 'interaction_required' ||
            msalError?.errorCode === 'login_required'
          ) {
            console.warn('API token consent required, triggering interactive login...');
            try {
              const response = await instance.acquireTokenPopup({
                ...apiRequest,
                account: user,
              });
              return response.accessToken;
            } catch (popupError: unknown) {
              const msalPopupError = popupError as { constructor?: { name?: string }; errorCode?: string; message?: string };
              console.error('Popup token acquisition failed:', {
                errorType: msalPopupError?.constructor?.name,
                errorCode: msalPopupError?.errorCode,
                errorMessage: msalPopupError?.message,
              });

              // If popup is blocked, fall back to redirect
              if (
                popupError instanceof BrowserAuthError ||
                msalPopupError?.errorCode === 'popup_window_error' ||
                msalPopupError?.errorCode === 'empty_window_error'
              ) {
                console.warn('Popup blocked, falling back to redirect...');
                sessionStorage.setItem('api_consent_redirect', window.location.pathname);
                await instance.acquireTokenRedirect({
                  ...apiRequest,
                  account: user,
                });
                return null; // Page will redirect
              }
              return null;
            }
          }
          console.error('Token refresh callback failed (unhandled error type):', error);
          return null;
        }
      });

      // Acquire initial token for API calls using API scope
      instance
        .acquireTokenSilent({
          ...apiRequest,
          account: user,
        })
        .then((response) => {
          apiClient.setAuthToken(response.accessToken);

          // Extract roles from token
          const payload = decodeJwtPayload(response.accessToken);
          if (payload && Array.isArray(payload.roles)) {
            setRoles(payload.roles as string[]);
          } else {
            setRoles([]);
          }
        })
        .catch((error) => {
          console.error('Failed to acquire token silently:', error);
          // If silent acquisition fails, try interactive
          instance.acquireTokenRedirect(apiRequest);
        });
    } else {
      apiClient.clearAuthToken();
      apiClient.setTokenRefreshCallback(null);
      setRoles([]);
    }
  }, [isAuthenticated, user, instance]);

  /**
   * Initiate login flow
   */
  const login = async (): Promise<void> => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  /**
   * Initiate logout flow
   */
  const logout = async (): Promise<void> => {
    try {
      apiClient.clearAuthToken();
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  /**
   * Get access token for API calls
   */
  const getAccessToken = async (): Promise<string | null> => {
    if (!user) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: user,
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      // Try interactive login if silent fails
      try {
        await instance.acquireTokenRedirect(apiRequest);
        return null; // Redirect will reload page
      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
        return null;
      }
    }
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    return roles.includes(role);
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    roles,
    hasRole,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

/**
 * AuthProvider component
 * Wrap your app with this to enable authentication
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [configValid, setConfigValid] = useState(true);

  useEffect(() => {
    setConfigValid(validateAuthConfig());
  }, []);

  if (!configValid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-red-50 p-4">
        <div className="rounded-lg bg-white p-6 shadow-lg">
          <h1 className="text-xl font-bold text-red-600">Configuration Error</h1>
          <p className="mt-2 text-gray-600">
            Missing Entra ID configuration. Please check your environment variables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
}

export { msalInstance };
